import { FC, memo } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { FieldArray } from 'formik';
// Import Constants
import l from 'constants/localizationLabels';
import { BUTTON_ROWS_FORM_KEY } from '../constants';
// Import Components
import { Alert, Button, ModuleTitle } from '@geneui/components';
import ButtonDetailsForm from './ButtonDetailsForm';
// Import Styles
import styles from './buttonRowsBuilder.module.scss';

interface Props {
    rowIndex: number;
    handleRemoveRow: (index: number) => () => void;
    onFocus: () => void;
}

const ButtonsRow: FC<Props> = ({ rowIndex, handleRemoveRow, onFocus }) => {
    const { t } = useTranslation();

    return (
        <FieldArray
            name={`${BUTTON_ROWS_FORM_KEY}.${rowIndex}`}
            render={({ form, push, handleRemove }) => {
                const buttons = form.values[BUTTON_ROWS_FORM_KEY][rowIndex];

                const handleAddButton = () => {
                    push({ url: '', text: '' });
                };

                const handleRemoveButton = (index: number) => {
                    if (buttons.length === 1) {
                        return handleRemoveRow(rowIndex);
                    } else {
                        return handleRemove(index);
                    }
                };

                return (
                    <div className={classNames(styles.container, styles.withTitle)}>
                        <div className={classNames(styles.wrapper, styles.nested, styles.withTitle)}>
                            <ModuleTitle
                                title={t(l.ButtonsRow, {
                                    index: rowIndex + 1,
                                    total: form.values[BUTTON_ROWS_FORM_KEY].length,
                                })}
                            >
                                <Button
                                    flexibility="default"
                                    appearance="minimal"
                                    icon="bc-icon-trash"
                                    onClick={handleRemoveRow(rowIndex)}
                                />
                            </ModuleTitle>
                            <div className={styles.nestedFieldsWrapper}>
                                <div className={styles.actionButtons}>
                                    <Button
                                        flexibility="default"
                                        appearance="outline"
                                        size="medium"
                                        icon="bc-icon-plus"
                                        onClick={handleAddButton}
                                        disabled={buttons.length >= 4}
                                    >
                                        {t(l.AddButton)}
                                    </Button>
                                </div>
                                <div className={classNames(styles.fieldsContainer, { [styles.nested]: false })}>
                                    <>
                                        {buttons.map((_, buttonIndex) => (
                                            <ButtonDetailsForm
                                                key={buttonIndex}
                                                rowIndex={rowIndex}
                                                buttonIndex={buttonIndex}
                                                handleRemove={handleRemoveButton}
                                                onFocus={onFocus}
                                            />
                                        ))}
                                        {buttons.length === 0 && (
                                            <Alert
                                                closable={false}
                                                title={t(l.ButtonsRowDescription)}
                                                type="info"
                                                className="partner-group-alert"
                                            />
                                        )}
                                    </>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            }}
        />
    );
};

export default memo(ButtonsRow);
